// Copyright (c) 2025. CYBERKOALA LLC.
//
// SPDX-License-Identifier: Proprietary License.
//
// Redistribution and use in source and binary forms, with or without
// modification, are restricted.

import * as React from 'react';
import { useRef, useState } from 'react';
import { Participant, Track } from 'livekit-client';
import type { ParticipantClickEvent, TrackReferenceOrPlaceholder } from '@livekit/components-core';
import { isTrackReference, isTrackReferencePinned } from '@livekit/components-core';
import {
  ConnectionQualityIndicator,
  LockLockedIcon,
  ParticipantContext,
  ParticipantPlaceholder,
  TrackMutedIndicator,
  TrackRefContext,
  useEnsureParticipant,
  useEnsureTrackRef,
  useFeatureContext,
  useIsEncrypted,
  useMaybeLayoutContext,
  useMaybeParticipantContext,
  useMaybeTrackRefContext,
  useParticipantTile,
} from '@livekit/components-react';
import { ParticipantName } from './participant-name';
import ParticipantPlaceholderAvatar from './participant-placeholder';
import { FocusToggle } from './focus-toggle';
import '@/styles/volume-slider.css';
import { VideoTrack } from './video-track';
import { avatarPlaceholder } from '@/lib/avatar-placeholder';
import { useSocket } from '@/context/socket-context/use-external-socket';
import { useCall } from '@/context/call-context/use-call';
import { FaHand } from 'react-icons/fa6';
import UserAgentInfo from './hooks/user-agent-info';
import { deviceID } from '../presence/presence-pinger';
import type { OnlineUserDevice } from '../presence/presence-list';
import { useVolume } from '@/context/call-context/use-volume';
import { PiSpeakerSimpleHighFill, PiSpeakerSimpleSlashFill } from 'react-icons/pi';
import { cn } from '@/lib/utils.ts';
import { useGlobalSFX } from '@/context/sfx-context/use-sfx.ts';
import { handleScreenShareMuteOnHide } from '@/components/call/utils/handle-screen-share-mute-on-hide.ts';
import {
  ContextMenu,
  ContextMenuContent,
  ContextMenuItem,
  ContextMenuPortal,
  ContextMenuSeparator,
  ContextMenuTrigger,
} from '@radix-ui/react-context-menu';
import { useTranslation } from 'react-i18next';
import { ChannelType } from '@/models';
import { useTheme } from '@/hooks/use-theme.ts';
import { getAvatarColor, getAvatarMetaBg, getAvatarTextColor } from '@/lib/avatar-color';
import { Maximize2, ScreenShareIcon, VideoIcon, VideoOffIcon } from 'lucide-react';
import { SettingsMenuToggle } from '@/components/call/controls/settings-menu-toggle.tsx';
import { BsPersonVideo } from 'react-icons/bs';
import { useTrackAudioLevel } from '@/hooks/use-track-audio-level.ts';
import { ScreenShareAudioBorder } from '@/components/call/screen-share-audio-border.tsx';

type Reaction = {
  id: number;
  emoji: string;
  participantIdentity: string;
};

export function ParticipantContextIfNeeded(
  props: React.PropsWithChildren<{
    participant?: Participant;
  }>
) {
  const hasContext = !!useMaybeParticipantContext();
  return props.participant && !hasContext ? (
    <ParticipantContext.Provider value={props.participant}>
      {props.children}
    </ParticipantContext.Provider>
  ) : (
    <>{props.children}</>
  );
}

/**
 * Only create a `TrackRefContext` if there is no `TrackRefContext` already.
 */
function TrackRefContextIfNeeded(
  props: React.PropsWithChildren<{
    trackRef?: TrackReferenceOrPlaceholder;
  }>
) {
  const hasContext = !!useMaybeTrackRefContext();
  return props.trackRef && !hasContext ? (
    <TrackRefContext.Provider value={props.trackRef}>{props.children}</TrackRefContext.Provider>
  ) : (
    <>{props.children}</>
  );
}

/**
 * A good safe range:
 *
 * threshold: 0.01 to 0.03
 * peak: 0.08 to 0.2
 *
 * TODO: tweak it
 */
const screenShareAudioBorderOptions = {
  threshold: 0.03,
  peak: 0.1,
  attack: 0.35,
  release: 0.14,
  holdMs: 240,
};

/** @public */
export interface ParticipantTileProps extends React.HTMLAttributes<HTMLDivElement> {
  /** The track reference to display. */
  trackRef?: TrackReferenceOrPlaceholder;
  disableSpeakingIndicator?: boolean;
  participantsCount?: number;
  onParticipantClick?: (event: ParticipantClickEvent) => void;
  onPinClick?: () => void;
  focus?: boolean;
  channelType: ChannelType;
}

/**
 * The `ParticipantTile` component is the base utility wrapper for displaying a visual representation of a participant.
 * This component can be used as a child of the `TrackLoop` component or by passing a track reference as property.
 *
 * @example Using the `ParticipantTile` component with a track reference:
 * ```tsx
 * <ParticipantTile trackRef={trackRef} />
 * ```
 * @example Using the `ParticipantTile` component as a child of the `TrackLoop` component:
 * ```tsx
 * <TrackLoop>
 *  <ParticipantTile />
 * </TrackLoop>
 * ```
 * @public
 */
export function ParticipantTile({
  trackRef,
  children,
  onParticipantClick,
  disableSpeakingIndicator,
  participantsCount,
  focus,
  channelType,
  ...htmlProps
}: ParticipantTileProps) {
  const { effectiveTheme } = useTheme();
  const containerRef = useRef<HTMLDivElement>(null);
  const [isWideEnough, setIsWideEnough] = useState(true);
  const trackReference = useEnsureTrackRef(trackRef);
  const { t } = useTranslation();
  const participant = trackReference.participant;
  const videoRef = React.useRef<HTMLVideoElement>(null);
  const { activeCall } = useCall();
  const socket = useSocket();
  const { elementProps } = useParticipantTile<HTMLDivElement>({
    htmlProps,
    disableSpeakingIndicator,
    onParticipantClick,
    trackRef: trackReference,
  });
  const isEncrypted = useIsEncrypted(trackReference.participant);
  const layoutContext = useMaybeLayoutContext();
  const autoManageSubscription = useFeatureContext()?.autoSubscription;
  const [micState, setMicState] = React.useState(participant.isMicrophoneEnabled);
  const [isSpeaking, setIsSpeaking] = React.useState(participant.isSpeaking);
  const [raisedHand, setRaisedHand] = React.useState(false);
  const [avatarLoaded, setAvatarLoaded] = React.useState(false);
  const [imageSrc, setImageSrc] = React.useState(avatarPlaceholder({ name: participant.identity }));
  const [emojiReactions, setEmojiReactions] = React.useState<Reaction[]>([]);
  const { setVolume, getVolume, positions } = useVolume();
  const participantId = trackReference.participant.identity;
  const sfx = useGlobalSFX();
  const [isHovered, setIsHovered] = React.useState(false);
  const [isLandscape, setIsLandscape] = React.useState(false);
  const participantContext = useEnsureParticipant(participant);
  const audioLevel = participantContext.audioLevel;
  // const participantMetadata = participant.metadata;
  const participantIdentityWithoutMetadata = participant.identity.split('_')[0]; // removing metadata
  const participantAttributes = participant.attributes || {};
  const id = participantAttributes['id'];
  // const role = participantAttributes['role'];
  const avatar = participantAttributes['avatar'];
  // const ip = participantAttributes['ip'];
  const deviceType = participantAttributes['deviceType'];
  // const deviceModel = participantAttributes['deviceModel'];
  // const engineName = participantAttributes['engineName'];
  const osName = participantAttributes['osName'];
  const browserName = participantAttributes['browserName'];
  const [reactionCooldown, setReactionCooldown] = useState(false);
  const isLocalParticipant = participant?.isLocal;
  const isNotScreenShare = trackReference.source !== Track.Source.ScreenShare;
  const isScreenShare = trackReference.source === Track.Source.ScreenShare;
  const isCamera = trackReference.source === Track.Source.Camera;
  const isUnknown = trackReference.source === Track.Source.Unknown;
  const effectiveId = isScreenShare ? `${id}_screenShare` : id;
  const volume = getVolume(effectiveId);
  const [previousVolume, setPreviousVolume] = React.useState(volume);
  const [scrollFeedback, setScrollFeedback] = React.useState<string | null>(null);
  const feedbackTimeout = React.useRef<NodeJS.Timeout | null>(null);
  const isMutedOrSilent = !micState || volume === 0 || !participant.isMicrophoneEnabled;
  const [isVideoHidden, setIsVideoHidden] = React.useState(false);
  const isPinned = layoutContext && isTrackReferencePinned(trackReference, layoutContext.pin.state);
  const filledColor = effectiveTheme === 'dark' ? '#ffffff' : '#ffffff';
  const unfilledColor = effectiveTheme === 'dark' ? '#4b5563' : '#4b5563';

  const colorKey = React.useMemo(() => {
    if (typeof effectiveId === 'string' && effectiveId.length > 0) {
      return effectiveId;
    }

    if (typeof participantIdentityWithoutMetadata === 'string') {
      return participantIdentityWithoutMetadata;
    }

    return `participant-${trackReference?.source ?? 'unknown'}`;
  }, [effectiveId, participantIdentityWithoutMetadata, trackReference?.source]);

  const { bgColor, textColor, metaBg } = React.useMemo(() => {
    const bg = getAvatarColor(colorKey, effectiveTheme as 'dark' | 'light');
    const text = getAvatarTextColor(bg, effectiveTheme as 'dark' | 'light');
    const meta = getAvatarMetaBg(bg);

    return {
      bgColor: bg,
      textColor: text,
      metaBg: meta,
    };
  }, [colorKey, effectiveTheme]);

  function findScreenShareAudioPublication(participant: Participant) {
    return Array.from(participant.getTrackPublications()).find(
      (pub) => pub.source === Track.Source.ScreenShareAudio && pub.kind === Track.Kind.Audio
    );
  }

  function findScreenShareVideoPublication(participant: Participant) {
    return Array.from(participant.getTrackPublications()).find(
      (pub) => pub.source === Track.Source.ScreenShare && pub.kind === Track.Kind.Video
    );
  }

  const handleSubscribe = React.useCallback(
    (subscribed: boolean) => {
      if (
        trackReference.source &&
        !subscribed &&
        layoutContext &&
        layoutContext.pin.dispatch &&
        isTrackReferencePinned(trackReference, layoutContext.pin.state)
      ) {
        layoutContext.pin.dispatch({ msg: 'clear_pin' });
      }
    },
    [trackReference, layoutContext]
  );

  const pinTrack = React.useCallback(() => {
    if (
      isTrackReference(trackReference) &&
      (trackReference.publication?.kind === 'video' ||
        trackReference.source === Track.Source.Camera ||
        trackReference.source === Track.Source.ScreenShare) &&
      layoutContext &&
      layoutContext.pin.dispatch &&
      participantsCount &&
      participantsCount > 0
    ) {
      const isPinned = isTrackReferencePinned(trackReference, layoutContext.pin.state);
      layoutContext.pin.dispatch({
        msg: isPinned ? 'clear_pin' : 'set_pin',
        trackReference: trackReference,
      });
    }
  }, [trackReference, layoutContext]);

  const screenShareAudioPub = findScreenShareAudioPublication(participant);
  const mediaStreamTrack = screenShareAudioPub?.track?.mediaStreamTrack;

  const screenShareAudioLevel = useTrackAudioLevel(mediaStreamTrack);
  const hasScreenShareAudio = screenShareAudioLevel > 0.05; // Порог срабатывания (5%)

  function toggleMute(
    effectiveId: string,
    volume: number,
    setVolume: (id: string, vol: number) => void,
    previousVolume: number,
    setPreviousVolume: (v: number) => void
  ) {
    if (volume > 0) {
      setPreviousVolume(volume);
      setVolume(effectiveId, 0);
    } else {
      setVolume(effectiveId, previousVolume || 1); // TODO: think about it
    }
  }

  const handleEmojiReaction = (emoji: string) => {
    if (!socket || !activeCall?.chatId || reactionCooldown) return;

    socket.send(
      JSON.stringify({
        type: 'send-emoji-reaction',
        deviceId: deviceID,
        channelId: activeCall.chatId,
        emoji: emoji,
        participantIdentity: participantIdentityWithoutMetadata,
      })
    );
    setReactionCooldown(true);
    setTimeout(() => setReactionCooldown(false), 1000);
  };

  const percent = volume * 100;

  const color = (() => {
    const clamped = Math.max(0, Math.min(100, percent));
    const r = Math.round((clamped / 100) * 255);
    const g = Math.round(255 - (clamped / 100) * 255);
    return `rgb(${r}, ${g}, 0)`;
  })();

  // TODO: get prevous volume from userSettings, now its always 100% on hide/unhide screenShare video
  function handleHideVideo(participantId: string, hidden: boolean) {
    handleScreenShareMuteOnHide({
      id: id, // only id passed, id_screenShare
      hidden,
      getVolume,
      setVolume,
      previousVolume,
      setPreviousVolume,
    });
  }

  function toggleHideVideo() {
    const next = !isVideoHidden;
    setIsVideoHidden(next);
    handleHideVideo(participantId, next);
  }

  React.useEffect(() => {
    // Синхронизируем previousVolume с volume, когда видео не скрыто
    // Это необходимо, чтобы при показе скрытого видео восстанавливался актуальный звук из userSettings
    if (!isVideoHidden && previousVolume !== volume) {
      setPreviousVolume(volume);
    }
  }, [volume, isVideoHidden]);

  React.useEffect(() => {
    if (avatar) {
      const img = new Image();
      img.src = avatar;
      img.onload = () => {
        setImageSrc(avatar);
        setAvatarLoaded(true);
      };
    }
  }, [avatar]);

  React.useEffect(() => {
    if (!socket) return;

    // Send speaking state immediately on change
    socket.send(
      JSON.stringify({
        type: 'update-speaking-state',
        deviceId: deviceID,
        channelId: activeCall?.chatId,
        participantIdentity: participantIdentityWithoutMetadata,
        speaking: isSpeaking,
      })
    );

    let intervalId: NodeJS.Timeout | null = null;
    if (isSpeaking) {
      intervalId = setInterval(() => {
        socket.send(
          JSON.stringify({
            type: 'update-speaking-state',
            deviceId: deviceID,
            channelId: activeCall?.chatId,
            participantIdentity: participantIdentityWithoutMetadata,
            speaking: true,
          })
        );
      }, 500);
    }

    return () => {
      if (intervalId) clearInterval(intervalId);
    };
  }, [isSpeaking, participant.identity, socket, activeCall?.chatId]);

  React.useEffect(() => {
    if (!socket) return;

    const intervalId = setInterval(() => {
      socket.send(
        JSON.stringify({
          type: 'update-mic-state',
          deviceId: deviceID,
          channelId: activeCall?.chatId,
          participantIdentity: participantIdentityWithoutMetadata,
          mic: micState,
        })
      );
    }, 1_000);

    return () => clearInterval(intervalId);
  }, [participant.identity, socket, activeCall?.chatId, micState]);

  React.useEffect(() => {
    if (!socket) return;
    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data.type === 'channel-state-update' && data.channelId === activeCall?.chatId) {
        const state = data.states.find(
          (s: any) => s.participantIdentity === participantIdentityWithoutMetadata // todo id?
        );
        const hasRaisedHand = state?.devices?.some((device: OnlineUserDevice) => device.raisedHand);
        setRaisedHand(hasRaisedHand);
      }
    };

    socket.addEventListener('message', handleMessage);

    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [participant.identity, socket, activeCall?.chatId]);

  React.useEffect(() => {
    if (!socket) return;

    const handleMessage = (event: MessageEvent) => {
      const data = JSON.parse(event.data);

      if (data.type === 'emoji-reaction' && data.channelId === activeCall?.chatId) {
        if (data.participantIdentity === participantIdentityWithoutMetadata) {
          setEmojiReactions((prev) => [
            ...prev,
            {
              id: Date.now(),
              emoji: data.emoji,
              participantIdentity: participantIdentityWithoutMetadata,
            },
          ]);

          if (channelType === ChannelType.TRIBUNE || channelType === ChannelType.STREAM) return;

          if (data.emoji == '👏') sfx.clap.play();
          if (data.emoji == '😂') sfx.hahaLaughCartoonBully.play();
          if (data.emoji == '❤️') sfx.pupUpBubble.play();
          if (data.emoji == '🥁') sfx.punchlineDrumStingSoundroll.play();
        }
      }
    };

    socket.addEventListener('message', handleMessage);

    return () => {
      socket.removeEventListener('message', handleMessage);
    };
  }, [socket, activeCall?.chatId, participant.identity]);

  // Remove emoji reactions after they float out of view
  React.useEffect(() => {
    const interval = setInterval(() => {
      setEmojiReactions(
        (prev) => prev.filter((reaction) => Date.now() - reaction.id < 3000) // Keep reactions for 3 seconds
      );
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  React.useEffect(() => {
    const speaking = participant.isSpeaking;
    setIsSpeaking(speaking);
  }, [participant.isSpeaking]);

  React.useEffect(() => {
    setMicState(participant.isMicrophoneEnabled);
  }, [participant.isMicrophoneEnabled]);

  React.useEffect(() => {
    const mediaQuery = window.matchMedia('(orientation: landscape)');
    setIsLandscape(mediaQuery.matches);

    const handleOrientationChange = (e: any) => {
      setIsLandscape(e.matches);
    };

    mediaQuery.addEventListener('change', handleOrientationChange);

    return () => {
      mediaQuery.removeEventListener('change', handleOrientationChange);
    };
  }, []);

  React.useEffect(() => {
    if (!containerRef.current) return;
    const observer = new ResizeObserver(([entry]) => {
      setIsWideEnough(entry.contentRect.width > 240);
    });
    observer.observe(containerRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>
        <div
          ref={containerRef}
          style={
            {
              position: 'relative',
              '--tile-bg': bgColor,
              '--tile-text': textColor,
              '--tile-meta-bg': metaBg,
            } as React.CSSProperties
          }
          className="calls-participant-tile"
          {...elementProps}
          onDoubleClick={pinTrack}
          onMouseEnter={() => setIsHovered(true)}
          onMouseLeave={() => setIsHovered(false)}
          onPointerEnter={() => setIsHovered(true)}
          onPointerLeave={() => setIsHovered(false)}
          onTouchStart={() => setIsHovered(true)}
          onTouchEnd={() => setIsHovered(false)}
          onWheel={(e) => {
            if (!isHovered || isLocalParticipant) return;
            e.preventDefault();

            const delta = e.deltaY < 0 ? 0.05 : -0.05;
            const newVol = Math.min(1, Math.max(0, volume + delta));
            setVolume(effectiveId, newVol);
            if (newVol > 0) setPreviousVolume(newVol);

            const change = Math.round(delta * 100);
            setScrollFeedback(
              change > 0
                ? `Громкость ${(newVol * 100).toFixed(0)}%`
                : `Громкость ${(newVol * 100).toFixed(0)}%`
            );

            if (feedbackTimeout.current) clearTimeout(feedbackTimeout.current);
            feedbackTimeout.current = setTimeout(() => setScrollFeedback(null), 800);
          }}
        >
          <TrackRefContextIfNeeded trackRef={trackReference}>
            <ParticipantContextIfNeeded participant={trackReference.participant}>
              {children ?? (
                <>
                  <div className="participant-speaking-container relative flex size-full items-center justify-center overflow-hidden">
                    {isNotScreenShare && (
                      <div
                        style={{
                          borderWidth: isNotScreenShare
                            ? `${Math.min(16, audioLevel * 16)}px`
                            : '0px',
                          borderColor: isNotScreenShare
                            ? isMutedOrSilent
                              ? '#ef4444'
                              : '#22c55e'
                            : 'transparent',
                          borderStyle: 'solid',
                          borderRadius: '0.5em',
                          transition: 'border-width 0.1s ease-out',
                          pointerEvents: 'none',
                        }}
                        className="absolute inset-0 box-border"
                      />
                    )}
                    <div
                      className={`relative flex size-full items-center justify-center ${
                        isLandscape ? 'object-contain' : 'object-cover'
                      }`}
                    >
                      {!isVideoHidden &&
                      isTrackReference(trackReference) &&
                      (trackReference.publication?.kind === 'video' ||
                        trackReference.source === Track.Source.Camera ||
                        trackReference.source === Track.Source.ScreenShare) ? (
                        <VideoTrack
                          ref={videoRef}
                          trackRef={trackReference}
                          onSubscriptionStatusChanged={handleSubscribe}
                          manageSubscription={autoManageSubscription}
                          focus={focus}
                          showControls={true}
                          screenShareAudioLevel={isScreenShare ? screenShareAudioLevel : 0}
                          volume={volume}
                          volumePercent={percent}
                          volumeColor={color}
                          onVolumeChange={(newVol: number) => {
                            setVolume(effectiveId, newVol);
                            if (newVol > 0) setPreviousVolume(newVol);
                          }}
                          onMuteParticipant={() =>
                            toggleMute(
                              effectiveId,
                              volume,
                              setVolume,
                              previousVolume,
                              setPreviousVolume
                            )
                          }
                          localSettingsNode={
                            isLocalParticipant && trackReference.source === Track.Source.Camera ? (
                              <SettingsMenuToggle>
                                <BsPersonVideo className="text-white" />
                              </SettingsMenuToggle>
                            ) : null
                          }
                        />
                      ) : null}
                      <div
                        className={cn(
                          'absolute bottom-0 left-0 right-0 h-10 bg-gradient-to-t from-black/10 to-transparent pointer-events-none transition-opacity duration-200',
                          isScreenShare && isHovered ? 'opacity-100' : 'opacity-0'
                        )}
                        aria-hidden="true"
                      />
                      <div
                        className="lk-participant-placeholder participant-placeholder"
                        style={{
                          backgroundColor: bgColor,
                          color: textColor,
                          borderRadius: '0.5em',
                          margin: '0.3em',
                        }}
                      >
                        {participant ? (
                          <ParticipantPlaceholderAvatar
                            avatarUrl={imageSrc}
                            loaded={avatarLoaded}
                            isSpeaking={isSpeaking}
                            audioLevel={audioLevel}
                            mutedOrSilent={!micState || volume === 0}
                          />
                        ) : (
                          <ParticipantPlaceholder />
                        )}
                      </div>
                      {trackReference.source === Track.Source.ScreenShare &&
                        !trackReference.publication?.isSubscribed && (
                          <div className="absolute inset-0 z-20 flex m-1.5 flex-col items-center justify-center rounded-lg bg-black/80 text-white  transition-all duration-300">
                            <VideoOffIcon className="mb-2 h-10 w-10 text-stone-400" />
                            <span className="text-sm font-semibold tracking-wide">
                              Видео скрыто
                            </span>
                          </div>
                        )}
                      <div className="lk-participant-metadata pointer-events-none">
                        <div className="lk-participant-metadata-item">
                          {trackReference.source === Track.Source.Camera ? (
                            <>
                              {isEncrypted && <LockLockedIcon style={{ marginRight: '0.25rem' }} />}
                              <TrackMutedIndicator
                                trackRef={{
                                  participant: trackReference.participant,
                                  source: Track.Source.Microphone,
                                }}
                                show={'muted'}
                              ></TrackMutedIndicator>
                              <ParticipantName />
                            </>
                          ) : (
                            <>
                              {!isHovered && (
                                <>
                                  <ScreenShareIcon style={{ marginRight: '0.25rem' }} />
                                  <ParticipantName>&apos; {t('common.screen')}</ParticipantName>
                                </>
                              )}
                            </>
                          )}
                          {/*</>*/}
                          {/*)}*/}
                        </div>
                        <ConnectionQualityIndicator className="lk-participant-metadata-item" />
                      </div>
                      {raisedHand && (
                        <div className="absolute right-1 top-0 rounded-sm bg-foreground-50 p-1 hover:bg-foreground-50/70  pointer-events-none">
                          <FaHand className="size-4 text-yellow-500" />
                        </div>
                      )}
                      {isWideEnough && !isLocalParticipant && (
                        <div
                          className={`text-sm reaction-buttons rounded-sm bg-foreground-100/50 text-white hover:bg-foreground-100/70 ${
                            isHovered ? 'visible' : ''
                          } ${
                            isWideEnough
                              ? 'bottom-10 left-2 gap-2 scale-100'
                              : 'bottom-2 left-1 gap-0 scale-[0.8]'
                          }`}
                          style={{
                            position: 'absolute',
                            bottom: '2.6rem',
                            left: '0.5rem',
                            display: 'flex',
                            transformOrigin: 'left bottom',
                          }}
                        >
                          {['👏', '❤️', '😂', '🥁'].map((emoji) => (
                            <button
                              key={emoji}
                              onClick={() => handleEmojiReaction(emoji)}
                              className={cn(
                                'rounded transition-all duration-300',
                                reactionCooldown
                                  ? 'opacity-50 cursor-not-allowed'
                                  : 'hover:scale-110 active:scale-90',
                                isWideEnough ? 'text-base md:text-lg p-1' : 'text-sm p-[2px]'
                              )}
                              disabled={reactionCooldown}
                              title={reactionCooldown ? 'Подождите...' : 'Отправить реакцию'}
                            >
                              {emoji}
                            </button>
                          ))}
                        </div>
                      )}
                      {emojiReactions.map((reaction) => (
                        <div
                          key={reaction.id}
                          className={cn(
                            'animate-float-zigzag absolute bottom-0 right-2 -translate-x-1/2',
                            isWideEnough ? 'text-4xl' : 'text-2xl'
                          )}
                          style={{
                            animationDuration: '3s',
                            animationTimingFunction: 'ease-in-out',
                          }}
                        >
                          {reaction.emoji}
                        </div>
                      ))}
                      {isWideEnough && (
                        <div
                          className={`absolute top-0 rounded-sm bg-foreground-50/50 hover:bg-foreground-100/70 ${
                            isHovered ? 'visible opacity-40' : 'opacity-0'
                          }`}
                        >
                          <UserAgentInfo
                            deviceType={deviceType}
                            osName={osName}
                            browserName={browserName}
                            avatar={''}
                            role={'GUEST'}
                            ip={null}
                          />
                        </div>
                      )}
                      {scrollFeedback && (
                        <div
                          style={{
                            position: 'absolute',
                            top: '24px',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            background: 'rgba(0,0,0,0.75)',
                            color: 'white',
                            padding: '2px 6px',
                            borderRadius: '4px',
                            fontSize: '12px',
                            opacity: scrollFeedback ? 1 : 0,
                            transition: 'opacity 0.3s ease',
                            pointerEvents: 'none',
                          }}
                        >
                          {scrollFeedback}
                        </div>
                      )}
                      {/*TODO: think about suppressing in screen share*/}
                      {isHovered && !isLocalParticipant && !isScreenShare && (
                        // && !isCamera && !isUnknown
                        <div
                          style={{
                            position: 'absolute',
                            zIndex: 10,
                            backgroundColor: 'rgba(0, 0, 0, 0.5)',
                            borderRadius: '4px',
                            display: 'flex',
                            alignItems: 'center',
                            transformOrigin: 'center top',
                            padding: isWideEnough ? '0.3em' : '0.2em',
                            fontSize: isWideEnough ? '0.875rem' : '0.75rem',
                          }}
                          className={cn(
                            'overflow-hidden rounded flex items-center justify-center text-white transition-transform duration-200',
                            isWideEnough
                              ? 'flex-row top-10 left-[0.3em] right-[0.3em] gap-2 scale-100'
                              : 'top-1 left-[0.2em] right-[0.2em] gap-0 scale-[0.85]'
                          )}
                        >
                          {isWideEnough && (
                            <span className="text-white">{Math.round(volume * 100)}%</span>
                          )}
                          <input
                            id="thumb"
                            type="range"
                            min="0"
                            max="1"
                            step="0.01"
                            value={volume}
                            onChange={(e) => {
                              const newVol = parseFloat(e.target.value);
                              setVolume(effectiveId, newVol);
                              if (newVol > 0) setPreviousVolume(newVol);
                            }}
                            style={{
                              flexGrow: 1,
                              appearance: 'none',
                              height: isWideEnough ? '0.5em' : '0.4em',
                              borderRadius: '4px',
                              outline: 'none',
                              transition: 'background 0.3s ease',
                              // background: `linear-gradient(to right, ${color} ${percent}%, #444 ${percent}%)`,
                              background: `linear-gradient(to right, ${filledColor} ${percent}%, ${unfilledColor} ${percent}%)`,
                            }}
                          />
                          {isWideEnough && (
                            <button
                              onClick={() =>
                                toggleMute(
                                  effectiveId,
                                  volume,
                                  setVolume,
                                  previousVolume,
                                  setPreviousVolume
                                )
                              }
                              className="text-white hover:text-red-500"
                              title={volume > 0 ? 'Mute' : 'Unmute'}
                            >
                              {volume > 0 ? (
                                <PiSpeakerSimpleHighFill />
                              ) : (
                                <PiSpeakerSimpleSlashFill />
                              )}
                            </button>
                          )}
                        </div>
                      )}
                    </div>
                    {isScreenShare && (
                      <ScreenShareAudioBorder
                        participant={participantContext}
                        enabled={!disableSpeakingIndicator && !isVideoHidden}
                        silent={volume === 0}
                        options={screenShareAudioBorderOptions}
                      />
                    )}
                  </div>
                </>
              )}
              {/*trackReference.source === Track.Source.Unknown ||*/}
              {(trackReference.source === Track.Source.Camera ||
                trackReference.source === Track.Source.ScreenShare) && (
                <FocusToggle trackRef={trackReference} />
              )}
            </ParticipantContextIfNeeded>
          </TrackRefContextIfNeeded>
        </div>
      </ContextMenuTrigger>
      <ContextMenuPortal>
        <ContextMenuContent
          className={cn(
            'z-[9999] min-w-[200px] rounded-lg bg-neutral-800 p-3 text-white shadow-lg',
            'data-[state=open]:animate-in data-[state=open]:fade-in-0 data-[state=open]:zoom-in-95',
            'data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95',
            'duration-150 ease-out'
          )}
          alignOffset={5}
        >
          {!isLocalParticipant && (
            <>
              <div className="flex items-center gap-2 mb-2">
                <span className="text-sm w-[3em]">{Math.round(volume * 100)}%</span>
                <input
                  type="range"
                  min="0"
                  max="1"
                  step="0.01"
                  value={volume}
                  onChange={(e) => {
                    const newVol = parseFloat(e.target.value);
                    setVolume(effectiveId, newVol);
                    if (newVol > 0) setPreviousVolume(newVol);
                  }}
                  className="flex-grow h-1 rounded"
                  style={{
                    appearance: 'none',
                    transition: 'background 0.3s ease',
                    background: `linear-gradient(to right, ${filledColor} ${percent}%, ${unfilledColor} ${percent}%)`,
                    // background: `linear-gradient(to right, ${color} ${percent}%, #444 ${percent}%)`,
                  }}
                />
              </div>
              <ContextMenuSeparator className="my-1 h-px bg-neutral-700" />
              <ContextMenuItem
                onSelect={() =>
                  toggleMute(effectiveId, volume, setVolume, previousVolume, setPreviousVolume)
                }
                className={cn(
                  'flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm transition-colors hover:bg-neutral-700 focus:bg-neutral-700 focus:outline-none'
                )}
              >
                {volume > 0 ? (
                  <>
                    <PiSpeakerSimpleHighFill className="text-green-400" />{' '}
                    {t('participant_tile.toggle_sound_off')}
                  </>
                ) : (
                  <>
                    <PiSpeakerSimpleSlashFill className="text-red-400" />{' '}
                    {t('participant_tile.toggle_sound_on')}
                  </>
                )}
              </ContextMenuItem>
            </>
          )}
          {isTrackReference(trackReference) &&
            (trackReference.publication?.kind === 'video' ||
              trackReference.source === Track.Source.Camera ||
              trackReference.source === Track.Source.ScreenShare) && (
              <ContextMenuItem
                onSelect={toggleHideVideo}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm transition-colors hover:bg-neutral-700 focus:bg-neutral-700 focus:outline-none"
              >
                {isVideoHidden ? (
                  <>
                    <VideoIcon className="text-green-400" />
                    {t('participant_tile.show_video')}
                  </>
                ) : (
                  <>
                    <VideoOffIcon className="text-yellow-400" />
                    {t('participant_tile.hide_video')}
                  </>
                )}
              </ContextMenuItem>
            )}
          {/* TODO: fix it  isPinned*/}
          {!isLocalParticipant &&
            isTrackReference(trackReference) &&
            (trackReference.publication?.kind === 'video' ||
              trackReference.source === Track.Source.Camera ||
              trackReference.source === Track.Source.ScreenShare) &&
            !isPinned &&
            layoutContext?.pin?.dispatch && (
              <ContextMenuItem
                onSelect={pinTrack}
                className="flex cursor-pointer items-center gap-2 rounded px-2 py-1 text-sm transition-colors hover:bg-neutral-700 focus:bg-neutral-700 focus:outline-none"
              >
                <>
                  <Maximize2 className="text-blue-400" />
                  {t('participant_tile.focus')}
                </>
              </ContextMenuItem>
            )}
        </ContextMenuContent>
      </ContextMenuPortal>
    </ContextMenu>
  );
}
